import {
    Button,
    Group,
    Select,
    Stack,
    Table,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CityCollectionRO } from '@routellers/types/dist/city-collection/city-collection.ro';
import {
    CITY_COLLECTION_KINDS,
    CityCollectionKindEnum,
} from '@routellers/types/dist/city-collection/city-collection-kind.enum';
import { GuideStatusEnum } from '@routellers/types/dist/city-guide/enum/guide-status.enum';
import { CityAttractionRO } from '@routellers/types/dist/city-attraction/city-attraction.ro';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { CityQuickSelect, parseCityFilter } from '../components/city-quick-select';
import { PageHeader } from '../components/page-header';
import { enumLabel, enumOptions } from '../i18n';

const LANGUAGES = [Languages.RU, Languages.EN, Languages.IT, Languages.DE];

type LocaleDraft = {
    name: string;
    description: string;
    seoTitle: string;
    seoDescription: string;
};

const emptyLocale = (): LocaleDraft => ({
    name: '',
    description: '',
    seoTitle: '',
    seoDescription: '',
});

export const CollectionsPage = () => {
    const { t } = useTranslation();
    const [cityFilter, setCityFilter] = useState('all');
    const [rows, setRows] = useState<CityCollectionRO[]>([]);
    const [kind, setKind] = useState<CityCollectionKindEnum | null>(null);
    const [status, setStatus] = useState<GuideStatusEnum>(GuideStatusEnum.DRAFT);
    const [coverUrl, setCoverUrl] = useState('');
    const [lang, setLang] = useState<Languages>(Languages.RU);
    const [locales, setLocales] = useState<Partial<Record<Languages, LocaleDraft>>>({});
    const [items, setItems] = useState<Array<{ id: number; title: string }>>([]);
    const [search, setSearch] = useState('');
    const [found, setFound] = useState<CityAttractionRO[]>([]);
    const [saving, setSaving] = useState(false);

    const cityId = parseCityFilter(cityFilter);
    const current = rows.find((row) => row.kind === kind) || null;
    const draft = locales[lang] || emptyLocale();

    const load = async () => {
        if (!cityId) {
            setRows([]);
            return;
        }

        try {
            const { data } = await api.cityCollectionsApi.query({
                page: 1,
                limit: 50,
                where: { cityId },
            });

            setRows(data?.items || []);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        }
    };

    useEffect(() => {
        setKind(null);
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cityFilter]);

    useEffect(() => {
        if (!kind) return;

        const row = rows.find((item) => item.kind === kind);
        const next: Partial<Record<Languages, LocaleDraft>> = {};

        for (const locale of row?.locales || []) {
            next[locale.language] = {
                name: locale.name || '',
                description: locale.description || '',
                seoTitle: locale.seoTitle || '',
                seoDescription: locale.seoDescription || '',
            };
        }

        setStatus(row?.status || GuideStatusEnum.DRAFT);
        setCoverUrl(row?.coverUrl || '');
        setLocales(next);
        setItems(
            (row?.items || []).map((item) => ({
                id: item.attractionId,
                title: item.title,
            })),
        );
        setFound([]);
        setSearch('');
    }, [kind, rows]);

    const save = async () => {
        if (!cityId || !kind) return;

        setSaving(true);

        try {
            const { data } = await api.cityCollectionsApi.upsert({
                data: {
                    cityId,
                    kind,
                    status,
                    coverUrl: coverUrl.trim() || null,
                    attractionIds: items.map((item) => item.id),
                    locales: LANGUAGES.filter((language) => locales[language]).map((language) => ({
                        language,
                        name: locales[language]?.name || '',
                        description: locales[language]?.description || null,
                        seoTitle: locales[language]?.seoTitle || null,
                        seoDescription: locales[language]?.seoDescription || null,
                    })),
                },
            });

            if (data) {
                setRows((prev) => {
                    const rest = prev.filter((row) => row.id !== data.id && row.kind !== data.kind);

                    return [...rest, data];
                });
            }

            notifications.show({ color: 'green', message: t('pages.collections.saved') });
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const searchPlaces = async () => {
        if (!cityId || !search.trim()) return;

        try {
            const { data } = await api.cityAttractionsApi.query({
                page: 1,
                limit: 12,
                searchText: search.trim(),
                where: { cityId },
            });

            setFound(data?.items || []);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        }
    };

    return (
        <Stack gap="md">
            <PageHeader title={t('pages.collections.title')} />
            <Text c="dimmed" size="sm">
                {t('pages.collections.lede')}
            </Text>
            <CityQuickSelect value={cityFilter} onChange={setCityFilter} />
            {!cityId ? <Text size="sm">{t('pages.collections.pickCity')}</Text> : null}
            {cityId ? (
                <Table striped highlightOnHover withTableBorder>
                    <Table.Tbody>
                        {CITY_COLLECTION_KINDS.map((value) => {
                            const row = rows.find((item) => item.kind === value);

                            return (
                                <Table.Tr
                                    key={value}
                                    style={{ cursor: 'pointer' }}
                                    bg={kind === value ? 'var(--mantine-color-blue-light)' : undefined}
                                    onClick={() => setKind(value)}
                                >
                                    <Table.Td>
                                        {enumLabel(t, 'enums.collectionKind', value)}
                                    </Table.Td>
                                    <Table.Td>
                                        {row
                                            ? t('pages.collections.places', { count: row.items.length })
                                            : t('pages.collections.emptyKind')}
                                    </Table.Td>
                                    <Table.Td>{row?.status || ''}</Table.Td>
                                </Table.Tr>
                            );
                        })}
                    </Table.Tbody>
                </Table>
            ) : null}
            {kind && cityId ? (
                <Stack gap="sm">
                    <Group>
                        <Select
                            label={t('common.status', { defaultValue: 'Status' })}
                            data={enumOptions(
                                [GuideStatusEnum.DRAFT, GuideStatusEnum.PUBLISHED],
                                t,
                                'enums.guideStatus',
                            )}
                            value={status}
                            onChange={(value) =>
                                setStatus((value as GuideStatusEnum) || GuideStatusEnum.DRAFT)
                            }
                        />
                        <TextInput
                            label={t('pages.collections.cover')}
                            value={coverUrl}
                            onChange={(event) => setCoverUrl(event.currentTarget.value)}
                            style={{ flex: 1 }}
                        />
                    </Group>
                    <Select
                        data={LANGUAGES.map((language) => ({ value: language, label: language }))}
                        value={lang}
                        onChange={(value) => setLang((value as Languages) || Languages.RU)}
                    />
                    <TextInput
                        label={t('pages.collections.name')}
                        value={draft.name}
                        onChange={(event) =>
                            setLocales((prev) => ({
                                ...prev,
                                [lang]: { ...draft, name: event.currentTarget.value },
                            }))
                        }
                    />
                    <Textarea
                        label={t('pages.collections.description')}
                        value={draft.description}
                        minRows={3}
                        onChange={(event) =>
                            setLocales((prev) => ({
                                ...prev,
                                [lang]: { ...draft, description: event.currentTarget.value },
                            }))
                        }
                    />
                    <TextInput
                        label={t('pages.collections.seoTitle')}
                        value={draft.seoTitle}
                        onChange={(event) =>
                            setLocales((prev) => ({
                                ...prev,
                                [lang]: { ...draft, seoTitle: event.currentTarget.value },
                            }))
                        }
                    />
                    <Textarea
                        label={t('pages.collections.seoDescription')}
                        value={draft.seoDescription}
                        minRows={2}
                        onChange={(event) =>
                            setLocales((prev) => ({
                                ...prev,
                                [lang]: { ...draft, seoDescription: event.currentTarget.value },
                            }))
                        }
                    />
                    <Stack gap={4}>
                        {items.map((item, index) => (
                            <Group key={item.id} justify="space-between">
                                <Text size="sm">
                                    {index + 1}. {item.title}
                                </Text>
                                <Group gap={4}>
                                    <Button
                                        size="xs"
                                        variant="default"
                                        disabled={index === 0}
                                        onClick={() =>
                                            setItems((prev) => {
                                                const next = [...prev];
                                                const [moved] = next.splice(index, 1);

                                                next.splice(index - 1, 0, moved);

                                                return next;
                                            })
                                        }
                                    >
                                        ↑
                                    </Button>
                                    <Button
                                        size="xs"
                                        variant="default"
                                        disabled={index === items.length - 1}
                                        onClick={() =>
                                            setItems((prev) => {
                                                const next = [...prev];
                                                const [moved] = next.splice(index, 1);

                                                next.splice(index + 1, 0, moved);

                                                return next;
                                            })
                                        }
                                    >
                                        ↓
                                    </Button>
                                    <Button
                                        size="xs"
                                        color="red"
                                        variant="light"
                                        onClick={() =>
                                            setItems((prev) => prev.filter((place) => place.id !== item.id))
                                        }
                                    >
                                        {t('common.delete')}
                                    </Button>
                                </Group>
                            </Group>
                        ))}
                    </Stack>
                    <Group>
                        <TextInput
                            placeholder={t('pages.collections.searchPlace')}
                            value={search}
                            onChange={(event) => setSearch(event.currentTarget.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') searchPlaces();
                            }}
                            style={{ flex: 1 }}
                        />
                        <Button variant="default" onClick={searchPlaces}>
                            {t('common.search')}
                        </Button>
                    </Group>
                    {found.map((place) => (
                        <Group key={place.id} justify="space-between">
                            <Text size="sm">{place.displayTitle || place.title}</Text>
                            <Button
                                size="xs"
                                variant="light"
                                disabled={items.some((item) => item.id === place.id)}
                                onClick={() =>
                                    setItems((prev) => [
                                        ...prev,
                                        { id: place.id, title: place.displayTitle || place.title },
                                    ])
                                }
                            >
                                {t('pages.collections.addPlace')}
                            </Button>
                        </Group>
                    ))}
                    <Button loading={saving} onClick={save}>
                        {current ? t('common.save') : t('common.create')}
                    </Button>
                </Stack>
            ) : null}
        </Stack>
    );
};
